import React, { useState, useEffect, useCallback } from 'react'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { leadsService, type Lead } from '../../services/leads'
import { api } from '../../services/api'
import { useToast } from '../../hooks/useToast'
import {
  Users, Download, Trash2, Loader2, Search,
  Mail, Phone, ChevronLeft, ChevronRight, AlertTriangle
} from 'lucide-react'

interface ChatbotOption {
  id: string
  name: string
}

const Leads: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([])
  const [total, setTotal] = useState(0)
  const [chatbots, setChatbots] = useState<ChatbotOption[]>([])
  const [selectedBotId, setSelectedBotId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(0)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [showBulkConfirm, setShowBulkConfirm] = useState(false)
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const PAGE_SIZE = 25
  const { toast } = useToast()

  // Load chatbots on mount
  useEffect(() => {
    const loadChatbots = async () => {
      try {
        const response = await api.get('/chatbots')
        const bots = response.data?.chatbots || response.data || []
        setChatbots(bots.map((b: any) => ({ id: b.id, name: b.name })))
        if (bots.length > 0 && !selectedBotId) {
          setSelectedBotId(bots[0].id)
        }
      } catch {
        toast({ title: 'Error', description: 'Failed to load chatbots', variant: 'destructive' })
      }
    }
    loadChatbots()
  }, [])

  // Load leads when bot or page changes
  const loadLeads = useCallback(async () => {
    if (!selectedBotId) return
    setIsLoading(true)
    try {
      const data = await leadsService.list(selectedBotId, PAGE_SIZE, page * PAGE_SIZE)
      setLeads(data.leads)
      setTotal(data.total)
    } catch {
      toast({ title: 'Error', description: 'Failed to load leads', variant: 'destructive' })
    } finally {
      setIsLoading(false)
    }
  }, [selectedBotId, page])

  useEffect(() => {
    loadLeads()
  }, [loadLeads])

  // Reset page when switching bots
  useEffect(() => {
    setPage(0)
  }, [selectedBotId])

  const handleExportCsv = async () => {
    if (!selectedBotId) return
    setIsExporting(true)
    try {
      const blob = await leadsService.exportCsv(selectedBotId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `leads_${selectedBotId}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast({ title: 'Exported', description: 'CSV file downloaded', variant: 'success' })
    } catch {
      toast({ title: 'Error', description: 'Failed to export CSV', variant: 'destructive' })
    } finally {
      setIsExporting(false)
    }
  }

  const handleDelete = async (leadId: string) => {
    setDeletingId(leadId)
    try {
      await leadsService.deleteLead(leadId)
      setLeads(prev => prev.filter(l => l.id !== leadId))
      setTotal(prev => prev - 1)
      toast({ title: 'Deleted', description: 'Lead removed', variant: 'success' })
    } catch {
      toast({ title: 'Error', description: 'Failed to delete lead', variant: 'destructive' })
    } finally {
      setDeletingId(null)
    }
  }

  const handleBulkDelete = async () => {
    if (!selectedBotId) return
    setIsBulkDeleting(true)
    try {
      const result = await leadsService.bulkDelete(selectedBotId)
      setLeads([])
      setTotal(0)
      setPage(0)
      setShowBulkConfirm(false)
      toast({ title: 'Deleted', description: `${result.deleted} leads removed`, variant: 'success' })
    } catch {
      toast({ title: 'Error', description: 'Failed to delete leads', variant: 'destructive' })
    } finally {
      setIsBulkDeleting(false)
    }
  }

  // Filter leads client-side by search query
  const filteredLeads = searchQuery
    ? leads.filter(l =>
        (l.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (l.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (l.phone || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (l.notes || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : leads

  const totalPages = Math.ceil(total / PAGE_SIZE)
  const botName = chatbots.find(b => b.id === selectedBotId)?.name || ''

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Leads</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Contact information captured from chatbot conversations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{total} total</span>
        </div>
      </div>

      {/* Controls */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Bot selector */}
          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
              Chatbot
            </label>
            <select
              value={selectedBotId}
              onChange={(e) => setSelectedBotId(e.target.value)}
              className="w-full md:w-56 px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-ring"
            >
              {chatbots.map(bot => (
                <option key={bot.id} value={bot.id}>{bot.name}</option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div className="flex-grow">
            <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Filter by name, email, phone..."
                className="w-full pl-10 pr-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-ring"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              disabled={isExporting || total === 0}
              className="flex items-center gap-1.5"
            >
              {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBulkConfirm(true)}
              disabled={total === 0}
              className="flex items-center gap-1.5 text-red-600 border-destructive/30 hover:bg-destructive/5"
            >
              <Trash2 className="w-4 h-4" />
              Delete All
            </Button>
          </div>
        </div>
      </Card>

      {/* Bulk delete confirmation */}
      {showBulkConfirm && (
        <Card className="p-4 border-destructive/30 bg-destructive/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="flex-grow">
              <p className="text-sm font-medium text-destructive">
                Delete all {total} leads for "{botName}"?
              </p>
              <p className="text-xs text-destructive mt-1">This action cannot be undone.</p>
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={isBulkDeleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {isBulkDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Yes, delete all
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowBulkConfirm(false)}
                  disabled={isBulkDeleting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading leads...</span>
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Users className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-sm font-medium">
              {searchQuery ? 'No leads match your search' : 'No leads captured yet'}
            </p>
            <p className="text-xs mt-1">
              {searchQuery ? 'Try a different search term' : 'Leads will appear here when end users share their contact info'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Notes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {filteredLeads.map(lead => (
                  <tr key={lead.id} className="hover:bg-muted transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold">
                          {(lead.name || lead.email || '?')[0].toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-foreground">
                          {lead.name || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {lead.email ? (
                        <a
                          href={`mailto:${lead.email}`}
                          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                        >
                          <Mail className="w-3.5 h-3.5" />
                          {lead.email}
                        </a>
                      ) : (
                        <span className="text-sm text-muted-foreground/50">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {lead.phone ? (
                        <span className="text-sm text-foreground flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                          {lead.phone}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground/50">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <span className="text-sm text-muted-foreground truncate block">
                        {lead.notes || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                        {lead.source || 'chat'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {lead.created_at
                        ? new Date(lead.created_at).toLocaleDateString(undefined, {
                            month: 'short', day: 'numeric', year: 'numeric'
                          })
                        : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleDelete(lead.id)}
                        disabled={deletingId === lead.id}
                        className="text-muted-foreground hover:text-red-500 transition-colors p-1 rounded"
                        title="Delete lead (GDPR)"
                      >
                        {deletingId === lead.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Trash2 className="w-4 h-4" />
                        }
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-border bg-muted">
            <span className="text-sm text-muted-foreground">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}

export default Leads
